import { logger } from '@adornis/base/logging.js';
import { subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { useMemo, useState } from 'react';
import { confirmAccountDeletionMutation } from '../../../../db/api/delete-account.js';
import { router } from '../../../App.js';
import { Button } from '../../../components/ui/button.js';
import { BlurredBackdrop } from '../../../components/ui/modal-backdrop.js';
import { clearGuestConversationId, clearGuestMessageCount } from '../../../lib/guest-user-id.js';
import { consumePendingJoinCode } from '../../../lib/pending-join-code.js';
import { discardPendingResultLink } from '../../../lib/pending-result-link.js';
import Home from '../../page.js';

/**
 * The link from the "Konto löschen" mail (db/api/delete-account.ts). Nothing is deleted on page load: mail
 * providers open links in advance to scan them, so the deletion waits for the explicit button.
 */
export default function DeleteAccountPage() {
  const token = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('token') ?? '';
  }, []);

  const [status, setStatus] = useState<'confirm' | 'deleting' | 'deleted' | 'error'>(token ? 'confirm' : 'error');

  const handleDelete = async () => {
    setStatus('deleting');
    try {
      await confirmAccountDeletionMutation(token)();
      // Nothing of the account stays behind in this browser either.
      clearGuestConversationId();
      clearGuestMessageCount();
      consumePendingJoinCode();
      discardPendingResultLink();
      // The server signed this session out; pick that up so the app no longer shows the account.
      await subscribeCurrentUser();
      setStatus('deleted');
    } catch (err) {
      logger.error(err, 'Account deletion failed');
      setStatus('error');
    }
  };

  return (
    <BlurredBackdrop background={<Home />}>
      <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">Konto löschen</h1>

        {status === 'confirm' || status === 'deleting' ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Dein Konto wird endgültig gelöscht – mit deinen Klausuren, Chats und Dateien. Das lässt sich nicht
              rückgängig machen.
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Als Lehrkraft: Auch alle deine Klassen und die Abgaben darin werden gelöscht; deine Schüler verlieren ihre
              Klasse und die für sie freigegebenen Ergebnisse.
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Als Schüler: Deine Klassenmitgliedschaften und die für dich freigegebenen Ergebnisse werden entfernt.
              Arbeiten, die du in der Klausur einer Lehrkraft abgegeben hast, bleiben bei der Lehrkraft.
            </p>
            <Button
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={status === 'deleting'}
              className="w-full"
            >
              {status === 'deleting' ? 'Konto wird gelöscht...' : 'Konto endgültig löschen'}
            </Button>
            <Button
              variant="outline"
              onClick={() => router.goTo('home')}
              disabled={status === 'deleting'}
              className="w-full"
            >
              Abbrechen
            </Button>
          </div>
        ) : status === 'deleted' ? (
          <div className="space-y-4">
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
              Dein Konto wurde gelöscht. Wir haben dir eine Bestätigung per E-Mail geschickt.
            </p>
            <Button onClick={() => router.goTo('home')} className="w-full bg-brand-500 hover:bg-[brand-600] text-white">
              Zu Gradias
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-red-600">
              Dieser Link ist ungültig oder abgelaufen. Du kannst die Löschung in den Einstellungen erneut anfordern.
            </p>
            <Button onClick={() => router.goTo('home')} className="w-full bg-brand-500 hover:bg-[brand-600] text-white">
              Zu Gradias
            </Button>
          </div>
        )}
      </div>
    </BlurredBackdrop>
  );
}
